/**
 * Setup screen — optional first-time setup (name, timezone, language).
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  Alert,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import { useAuthStore } from '../../src/stores/authStore';
import { useSettingsStore } from '../../src/stores/settingsStore';
import { getLanguages, LANG_ENGLISH_NAMES } from '../../src/utils/constants';
import { useTheme, type ThemeColors } from '../../src/utils/theme';
import { FONT_SIZE, SPACING, RADIUS } from '../../src/utils/design-tokens';
import { markOnboardingDone } from '../../src/utils/onboarding';
import WelcomeCarousel from '../../src/components/onboarding/WelcomeCarousel';
import LanguagePickerModal from '../../src/components/LanguagePickerModal';

export default function SetupScreen() {
  const router = useRouter();
  const { inferredTz } = useLocalSearchParams<{ inferredTz?: string }>();
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const {
    changeUiLanguage,
    changeServerLanguage,
    changeTimezone: setTz,
    detectTimezone,
  } = useSettingsStore();
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);

  const [name, setName] = useState(user?.displayName || '');
  const [selectedLang, setSelectedLang] = useState(user?.lang || 'en');
  const [timezone, setTimezone] = useState(inferredTz || user?.tz || 'Asia/Beirut');
  const [isDetecting, setIsDetecting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showCarousel, setShowCarousel] = useState(false);
  const [showLangPicker, setShowLangPicker] = useState(false);

  const handleDetectTimezone = async () => {
    setIsDetecting(true);
    try {
      const deviceTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setTimezone(deviceTz);
    } catch {
      Alert.alert(t('common.error'), t('settings.detectFailed'));
    } finally {
      setIsDetecting(false);
    }
  };

  useEffect(() => {
    // Only auto-detect from device if no country-based timezone was inferred
    if (!inferredTz) {
      handleDetectTimezone();
    }
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (name.trim()) {
        const { updateDisplayName } = useSettingsStore.getState();
        await updateDisplayName(name.trim());
      }
      if (selectedLang !== i18n.language) {
        await changeUiLanguage(selectedLang);
      }
      if (selectedLang !== (user?.lang || 'en')) {
        await changeServerLanguage(selectedLang);
      }
      if (timezone !== (user?.tz || 'Asia/Beirut')) {
        await setTz(timezone);
      }
      setShowCarousel(true);
    } catch {
      Alert.alert(t('common.error'), t('common.tryAgain'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkip = () => {
    setShowCarousel(true);
  };

  const handleCarouselDone = async () => {
    await markOnboardingDone();
    router.replace('/(tabs)/chat');
  };

  if (showCarousel) {
    return <WelcomeCarousel onDone={handleCarouselDone} />;
  }

  return (
    <ScrollView style={s.container} contentContainerStyle={s.content}>
      {/* Progress stepper: Phone → OTP → Setup */}
      <View style={s.stepper}>
        <View style={[s.stepDot, s.stepDone]} />
        <View style={[s.stepLine, s.stepLineDone]} />
        <View style={[s.stepDot, s.stepDone]} />
        <View style={[s.stepLine, s.stepLineDone]} />
        <View style={[s.stepDot, s.stepActive]} />
      </View>

      <Text style={s.title}>{t('auth.setupTitle')}</Text>
      <Text style={s.subtitle}>{t('auth.setupSubtitle')}</Text>

      {/* Name */}
      <View style={s.section}>
        <Text style={s.label}>{t('settings.name')}</Text>
        <TextInput
          style={s.input}
          value={name}
          onChangeText={setName}
          placeholder={t('auth.namePlaceholder')}
          placeholderTextColor={colors.textSecondary}
        />
      </View>

      {/* Language */}
      <View style={s.section}>
        <Text style={s.label}>{t('settings.language')}</Text>
        <TouchableOpacity
          style={s.langSelector}
          onPress={() => setShowLangPicker(true)}
          activeOpacity={0.7}
        >
          <Text style={s.langSelectorFlag}>{getLanguages().find(l => l.code === selectedLang)?.flag || '🌍'}</Text>
          <Text style={s.langSelectorLabel}>
            {getLanguages().find(l => l.code === selectedLang)?.label || selectedLang}
          </Text>
          <Text style={s.langSelectorEnglish}>
            {LANG_ENGLISH_NAMES[selectedLang] || ''}
          </Text>
          <Ionicons name="chevron-forward" size={16} color={colors.textSecondary} />
        </TouchableOpacity>
      </View>

      <LanguagePickerModal
        visible={showLangPicker}
        onClose={() => setShowLangPicker(false)}
        onSelect={(code) => setSelectedLang(code)}
        selectedCode={selectedLang}
        languages={getLanguages()}
        title={t('settings.language')}
      />

      {/* Timezone */}
      <View style={s.section}>
        <Text style={s.label}>{t('settings.timezone')}</Text>
        <View style={s.tzRow}>
          <Text style={s.tzText}>{timezone}</Text>
          <TouchableOpacity onPress={handleDetectTimezone} disabled={isDetecting}>
            {isDetecting ? (
              <ActivityIndicator size="small" color={colors.primary} />
            ) : (
              <Text style={s.detectButton}>{t('settings.detect')}</Text>
            )}
          </TouchableOpacity>
        </View>
      </View>

      {/* Buttons */}
      <TouchableOpacity
  onPress={handleSave}
  disabled={isSaving}
  accessibilityRole="button"
  accessibilityLabel={t('auth.letsGo')}
>
  <LinearGradient
    colors={isSaving ? [colors.textSecondary, colors.textSecondary] : ['#64B5F6', '#1E88E5']}
    start={{ x: 0, y: 0 }}
    end={{ x: 1, y: 1 }}
    style={s.button}
  >
    {isSaving ? (
      <ActivityIndicator color="#fff" />
    ) : (
      <View style={s.buttonContent}>
        <Text style={s.buttonText}>{t('auth.letsGo')}</Text>
        <Ionicons name="arrow-forward" size={18} color="#fff" />
      </View>
    )}
  </LinearGradient>
</TouchableOpacity>

      <TouchableOpacity onPress={handleSkip} style={s.skipButton} accessibilityRole="button" accessibilityLabel={t('auth.skipForNow')}>
        <Text style={s.skipText}>{t('auth.skipForNow')}</Text>
      </TouchableOpacity>
    </ScrollView>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  content: {
    paddingHorizontal: 32,
    paddingTop: 80,
    paddingBottom: 40,
  },
  title: {
    fontSize: FONT_SIZE.xl,
    fontFamily: 'PlusJakartaSans_700Bold',
    color: colors.text,
    textAlign: 'center',
    marginBottom: SPACING.sm,
  },
  subtitle: {
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter_400Regular',
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: SPACING['3xl'],
  },
  section: {
    marginBottom: SPACING.xl,
  },
  label: {
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter_600SemiBold',
    color: colors.text,
    marginBottom: SPACING.sm,
  },
  input: {
    backgroundColor: colors.card,
    borderRadius: RADIUS.md,
    paddingHorizontal: SPACING.base,
    paddingVertical: SPACING.md,
    fontSize: FONT_SIZE.base,
    fontFamily: 'Inter_400Regular',
    color: colors.text,
    borderWidth: 1,
    borderColor: colors.border,
  },
  langSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.card,
    borderRadius: RADIUS.md,
    paddingHorizontal: SPACING.base,
    paddingVertical: SPACING.md,
    borderWidth: 1,
    borderColor: colors.border,
    gap: SPACING.sm,
  },
  langSelectorFlag: {
    fontSize: 22,
  },
  langSelectorLabel: {
    fontSize: FONT_SIZE.base,
    fontFamily: 'Inter_600SemiBold',
    color: colors.text,
    flex: 1,
  },
  langSelectorEnglish: {
    fontSize: FONT_SIZE.sm,
    color: colors.textSecondary,
  },
  tzRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.card,
    borderRadius: RADIUS.md,
    paddingHorizontal: SPACING.base,
    paddingVertical: SPACING.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  tzText: {
    fontSize: FONT_SIZE.sm,
    color: colors.text,
  },
  detectButton: {
    fontSize: FONT_SIZE.sm,
    color: colors.primary,
    fontFamily: 'Inter_600SemiBold',
  },
  button: {
    backgroundColor: colors.primary,
    borderRadius: RADIUS.md,
    paddingVertical: SPACING.base,
    alignItems: 'center',
    marginTop: SPACING.base,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  buttonText: {
    color: '#fff',
    fontSize: FONT_SIZE.base,
    fontFamily: 'Inter_600SemiBold',
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  skipButton: {
    marginTop: SPACING.base,
    alignItems: 'center',
  },
  skipText: {
    fontSize: FONT_SIZE.sm,
    color: colors.textSecondary,
  },
  stepper: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
    gap: 0,
  },
  stepDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.border,
  },
  stepDone: {
    backgroundColor: colors.primary,
  },
  stepActive: {
    backgroundColor: colors.primary,
    width: 12,
    height: 12,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: colors.primaryLight,
  },
  stepLine: {
    width: 32,
    height: 2,
    backgroundColor: colors.border,
  },
  stepLineDone: {
    backgroundColor: colors.primary,
  },
});
